import { Flex, Avatar, Image, Text, Skeleton, Box } from '@chakra-ui/react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { FC, Fragment, useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import { PAGE_SIZE } from '@/components/ui/shared/chat/ChatPopup/components/static';
import { ChatProfile } from '@/components/ui/shared/chat/ChatPopup/components/types';
import { SearchInput as SharedSearchInput } from '@/components/ui/shared/inputs/SearchInput/SearchInput';
import { useLocalSearch } from '@/hooks/use-search/useLocalSearch';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { sessionQueryOptions } from '@/queries/upgrade/sessions';
import { thinProfileInfiniteNoSortQueryOptions } from '@/queries/upgrade/thinProfiles';
import { getProfileAvatar, getProfileFullName, getProfileTitle } from '@/utils/profileHelpers';

interface IUserList {
  setProfileSelected: (profile: ChatProfile) => void;
}

export const UserList: FC<IUserList> = ({ setProfileSelected }) => {
  const { searchTerm, ref: input } = useLocalSearch();
  const { data: session } = useQuery(sessionQueryOptions());
  const user = session?.user;
  const { eventId } = useQueryParams<{
    eventId: string;
  }>({
    eventId: StringParam,
  });

  const { data: infiniteData, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    thinProfileInfiniteNoSortQueryOptions(
      {
        query: searchTerm,
        with_ticket_for_event: eventId,
        searchable_attribute: ['first_name', 'last_name', 'institutions.name'],
      },
      null,
      PAGE_SIZE
    )
  );

  const items = useMemo(() => infiniteData?.pages?.flatMap((page) => page.data) ?? [], [infiniteData]);
  const isLoadingMore = isFetchingNextPage;
  const isReachingEnd = !hasNextPage;

  const itemsWithoutMe = items.filter((i: ChatProfile) => i.user?.id !== user?.id);

  const noResults = !isLoadingMore && itemsWithoutMe.length === 0 && searchTerm;

  return (
    <Fragment>
      <SharedSearchInput
        ref={input}
        w="full"
        bg="white"
        mt={6}
        color="gray.700"
        borderRadius="full"
        placeholder="search people"
      />
      <Flex flexDirection="column" overflowY="auto" flex={1} mt={5}>
        {noResults && <Text mt={2}>No search results found for &quot;{searchTerm}&quot;</Text>}
        {itemsWithoutMe.map((profile) => (
          <Flex
            onClick={() => setProfileSelected(profile)}
            _hover={{ boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.08)' }}
            cursor="pointer"
            mb={2}
            key={profile.id}
          >
            <Flex flexShrink={0}>
              {!getProfileAvatar(profile) && (
                <Avatar bg="violet.500" color="white" name={getProfileFullName(profile)} />
              )}
              {getProfileAvatar(profile) && (
                <Image
                  src={getProfileAvatar(profile)}
                  alt={getProfileFullName(profile)}
                  width={10}
                  height={10}
                  style={{ borderRadius: '50%' }}
                />
              )}
            </Flex>
            <Flex ml={2} flexDirection="column" justifyContent="center">
              <Text fontWeight="bold" fontFamily="heading">
                {getProfileFullName(profile)}
              </Text>
              <Text fontSize="xs">{getProfileTitle(profile)}</Text>
            </Flex>
          </Flex>
        ))}
        {isLoadingMore &&
          Array.from({ length: PAGE_SIZE }, (_, i) => (
            <Flex alignItems="center" mb={2} key={i}>
              <Skeleton borderRadius="50%" h={12} w={12} />
              <Flex ml={2} gap={2} flexDirection="column">
                <Skeleton w={40} h={3} />
                <Skeleton w={52} h={2} />
              </Flex>
            </Flex>
          ))}
        {!isLoadingMore && (
          <InView
            onChange={(inView) => {
              if (inView && !error && !isReachingEnd) {
                fetchNextPage();
              }
            }}
          >
            <Box w={1} h={1} />
          </InView>
        )}
      </Flex>
    </Fragment>
  );
};
